import { FileTextIcon, ReceiptTextIcon, TruckIcon } from "lucide-react";
import type * as React from "react";
import { useTranslation } from "react-i18next";
import type { DraftKind, DraftStatus } from "@steelbill/types";
import { ICON_TONE, TONE } from "@/features/invoices/DocumentStatusBadge";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { routes } from "@/routes/paths";

/**
 * How each kind of draft is shown — the icon and the words for it.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE SAME ICONS THE SIDEBAR AND THE REGISTERS USE, deliberately: a bill is a
 * receipt, a quotation is a sheet of paper, a challan is a truck. A draft is a
 * BILL that is not finished, not a third kind of object — so it wears the bill's
 * icon, and its unfinished-ness is carried by the amber `DRAFT/INV/12` badge
 * beside it.
 *
 * The drafts list used `PenLineIcon` for an invoice, which said "draft" a second
 * time and left the quotation's `FileTextIcon` as the only kind-bearing mark —
 * two rows a few pixels apart with near-identical glyphs.
 *
 * KEYED BY `DraftKind`, WHICH IS WHY THIS FILE EXISTS. The call sites were
 * ternaries (`kind === "INVOICE" ? … : …`), so `CHALLAN` — a real value of the
 * enum since 2026-08-15 — silently rendered as a QUOTATION: wrong icon, and the
 * words "Unfinished quotation" under a delivery challan. A `Record` makes that a
 * compile error instead. §17.7 SHIPPED ON 2026-08-15 and the challan editor
 * writes CHALLAN drafts today — this note used to say "§17.7 is unbuilt, so
 * nothing writes CHALLAN yet", which was the state when the entries were added
 * pre-emptively and is exactly why they were already right on the day it landed.
 *
 * ISSUED documents have their own copy of this idea now:
 * `features/documents/document-kind.tsx`, keyed by `DocumentKind`. The two are
 * deliberately separate maps — a draft is a saved FORM with no number, and its
 * destination is an EDITOR while a document's is a DETAIL screen.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const DRAFT_KIND_ICON: Record<DraftKind, React.ReactNode> = {
  INVOICE: <ReceiptTextIcon aria-hidden="true" className="size-4" />,
  QUOTATION: <FileTextIcon aria-hidden="true" className="size-4" />,
  CHALLAN: <TruckIcon aria-hidden="true" className="size-4" />,
};

/**
 * "Unfinished bill" / "Unfinished quotation" / "Unfinished delivery challan".
 *
 * IN WORDS, not only in the badge's `INV`/`QUOT`/`CHAL`. Those three letters are
 * English abbreviations on a screen that is often read in Marathi (§11.4), and
 * the badge is a name rather than a description.
 */
export const DRAFT_KIND_LABEL_KEY: Record<DraftKind, string> = {
  INVOICE: "draft.forInvoice",
  QUOTATION: "draft.forQuotation",
  CHALLAN: "draft.forChallan",
};

/**
 * Which editor "Continue" opens, per kind.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ONE SAVED FORM OPENS IN ONE SCREEN, and the wrong one loses work silently. A
 * quotation draft carries a subject, a validity and §9.3's tax-display mode; the
 * bill editor has no field for any of them, so it restores what it understands
 * and drops the rest on the next autosave. A challan draft carries a challan
 * type, a vehicle and a return date, and the same is true again.
 *
 * KEYED BY KIND, FOR THE REASON THIS FILE EXISTS. The home screen resolved this
 * with `kind === "INVOICE" ? invoiceNew : quotationNew`, which is correct for two
 * kinds and silently wrong for the third: a CHALLAN draft took the else-branch
 * and opened the QUOTATION editor. A ternary cannot be exhaustive, a
 * `Record<DraftKind, …>` cannot be anything else, and adding a fourth kind now
 * fails the build here rather than mis-routing a tap.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const DRAFT_RESUME_AT: Record<DraftKind, string> = {
  INVOICE: routes.invoiceNew,
  QUOTATION: routes.quotationNew,
  CHALLAN: routes.challanNew,
};

/**
 * The form's state IN WORDS, for the register's Status column.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THIS IS NOT A DUPLICATE OF THE BADGE, THOUGH IT SAYS THE SAME THING. The badge
 * reads `READY/QUOT/7`, and its `DRAFT`/`READY` lead is deliberately a CODE
 * TOKEN — untranslated, fixed, sayable out loud, exactly like `INV/26-27/67` on
 * a bill (see DraftNameBadge). Code tokens do not become Marathi.
 *
 * So on a screen read in Marathi (§11.4) the badge alone leaves the owner's own
 * language with nothing to say about the state of his work. This column is where
 * it gets said. It is the same split the register already makes one table down:
 * the number `INV/26-27/72` beside the status `Issued`.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const DRAFT_STATUS_LABEL_KEY: Record<DraftStatus, string> = {
  DRAFT: "draft.statusDraft",
  READY: "draft.statusReady",
};

/** `Continue` for one draft — the editor for its kind, carrying its id. */
export function draftResumeHref(kind: DraftKind, id: number | string): string {
  return `${DRAFT_RESUME_AT[kind]}?draft=${String(id)}`;
}

/**
 * The strip's heading and its one-line explanation, per kind.
 *
 * PER KIND BECAUSE THE STRIP IS SHARED. It said "Unfinished bills — started but
 * not made into bills yet" on the QUOTATIONS register until 2026-08-15: the
 * component was made kind-generic when it moved out of the bills register, and
 * the copy was not. The rows underneath read `DRAFT/QUOT/9`, so the heading
 * contradicted the very rows it sat above.
 */
export const DRAFT_LIST_TITLE_KEY: Record<DraftKind, string> = {
  INVOICE: "draft.listTitleInvoice",
  QUOTATION: "draft.listTitleQuotation",
  CHALLAN: "draft.listTitleChallan",
};

export const DRAFT_LIST_HELP_KEY: Record<DraftKind, string> = {
  INVOICE: "draft.listHelpInvoice",
  QUOTATION: "draft.listHelpQuotation",
  CHALLAN: "draft.listHelpChallan",
};

/**
 * The form's state, in the reader's own language and the register's own colours.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT WEARS `TONE`, THE PALETTE THE DOCUMENT BADGES USE, rather than colours of
 * its own. Drafts and documents sit in one list on the home screen and in two
 * stacked tables on every register; a second amber invented here would drift
 * from the register's the first time either was touched, and the owner would be
 * looking at two shades of "nothing is wrong yet" with no way to know they meant
 * the same thing.
 *
 * DRAFT IS `waiting` FOR THE SAME REASON AN INVOICE'S DRAFT IS: nothing has gone
 * wrong, something is simply not finished.
 *
 * READY IS `spent` FOR ITS COLOUR, NOT ITS NAME. `spent` is the neutral settled
 * grey — "over, without a verdict" — and READY is not over. But it is settled:
 * checked, with nothing left to do until somebody spends a number on it, so it
 * should stop competing for the eye exactly as that tone does. Green (`good`)
 * would sit it beside `Issued` and imply a document exists, which is the one
 * thing a draft is not.
 *
 * THE WORD CHANGES, NEVER THE COLOUR ALONE (§11.1).
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function DraftStatusBadge({
  status,
  className,
}: {
  status: DraftStatus;
  className?: string;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Badge
      variant="outline"
      className={cn(status === "READY" ? TONE.spent : TONE.waiting, className)}
    >
      {t(DRAFT_STATUS_LABEL_KEY[status])}
    </Badge>
  );
}

/**
 * The icon-tile classes for a DRAFT's state, so a draft row's tile matches its
 * badge exactly as a document row's does.
 *
 * The same two tones the badge above picks, resolved through the same
 * `ICON_TONE` map the documents use — one source for the colour, so a draft's
 * amber and an unsent quotation's amber can never drift apart.
 */
export function draftIconTone(status: DraftStatus): string {
  return status === "READY" ? ICON_TONE.spent : ICON_TONE.waiting;
}
